/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// Component imports
import { Button, Card, Checkbox, Col, Empty, Form, Input, Row, Select, Skeleton, Slider, Space, Tooltip, Typography } from 'antd';
import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import RichTextEditor from '../../../Forms/Fields/RichTextEditor/RichTextEditor';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { RICH_TEXT_TOOLBAR_ACTIONS } from '../../../../constants/markdown';
import { useFetchBotQuery } from '../../../../store/bots/botsApi';
import { NAV_BOT } from '../../../../constants/navigation';
import { PLUGIN_CATEGORY } from '../../../../constants/integrations';
import useTheme from '../../../../utils/hooks/useTheme';
import replaceUrlParams from '../../../../utils/string/replaceUrlParams';

const SemanticSearchNodeForm = ({ form, selectedElement }) => {
  const { t } = useTranslation();
  const { isEditingPrimaryLanguage } = useBotLanguage();
  const { borderRadiusBase } = useTheme();
  const { orgId, tenantId } = useParams();
  const navigate = useNavigate();

  const { data: bot, isFetching: isFetchingBot } = useFetchBotQuery({ orgId, tenantId });

  return (
    <Fragment>
      {
        isFetchingBot ? (
          <Skeleton active />
        ) : bot?.plugins?.filter((plugin) => plugin.category === PLUGIN_CATEGORY.LLM_SEMANTIC_SEARCH_NODE).length > 0 ? (
          <Fragment>
            <Form.Item
              label={t('Max Results')}
              name={['data', 'maxResults']}
            >
              <Slider
                min={1}
                max={25}
                defaultValue={10}
                className="custom-slider"
                marks={{ 1: '1', 10: 'Default', 25: '25' }}
                handleStyle={{ borderRadius: borderRadiusBase }}
              />
            </Form.Item>

            <Row justify={'center'} className="mb-4">
              <Col xs={24}>
                <Form.Item
                  name={['data', 'filterByTags']}
                  valuePropName="checked"
                  className="mb-1"
                >
                  <Checkbox defaultChecked={false} disabled={!isEditingPrimaryLanguage} >
                    <Space>
                      {t('Filter by Tags')}
                      <Tooltip title={ t('Train NLP on a specific subset of content based on tags.')}>
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </Space>
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col xs={20}>
                <Card
                  className={classNames('bg-layout-gray mb-3 shadow-none border-0', {
                    'opacity-50': !form.getFieldValue(['data', 'filterByTags']),
                    'opacity-1': form.getFieldValue(['data', 'filterByTags'])
                  })}
                >
                  <Form.Item
                    dependencies={['data', 'filterByTags']}
                    label={t('Included Tags')}
                    tooltip={{
                      icon: <QuestionCircleOutlined />,
                      title: t('Only Automated Answers that contain at least one of these tags will be included in the training dataset.')
                    }}
                    name={['data', 'includedTags']}
                  >
                    <Select
                      defaultValue={[]}
                      mode="tags"
                      size="large"
                      className="scrollable-tags-list"
                      disabled={!isEditingPrimaryLanguage || !form.getFieldValue(['data', 'filterByTags'])}
                      tokenSeparators={[',']}
                      placeholder={t('Add tags for the article')}
                      options={
                        form.getFieldValue(['data', 'includedTags'])?.length > 0 ? form.getFieldValue(['data', 'includedTags'])?.map((tag) => {
                          return {
                            value: tag,
                            label: tag
                          };
                        }) : []
                      }
                      maxTagPlaceholder={(omittedValues) => (
                        <Tooltip
                          title={
                            <div
                              className="overflow-y-auto overflow-x-hidden scrollbar-custom"
                              style={{ maxHeight: 220, maxWidth: 220 }}
                            >
                              {
                                omittedValues.map((value, index) => (
                                  <Typography.Paragraph ellipsis key={index} className="fs-base text-muted mb-0">
                                    {value.label}

                                    <Button
                                      size="small"
                                      type="link"
                                      className="mb-1"
                                      icon={<CloseOutlined className="text-secondary mr-0 fs-sm" />}
                                    />
                                  </Typography.Paragraph>
                                ))
                              }
                            </div>
                          }
                          placement="bottomLeft"
                        >
                          +{omittedValues.length}
                        </Tooltip>
                      )}
                    />
                  </Form.Item>

                  <Form.Item
                    dependencies={['data', 'filterByTags']}
                    label={t('Excluded Tags')}
                    tooltip={{
                      icon: <QuestionCircleOutlined />,
                      title: t('Any activity containing one or more of these tags will be excluded from training.')
                    }}
                    name={['data', 'excludedTags']}
                  >
                    <Select
                      mode="tags"
                      size="large"
                      defaultValue={[]}
                      className="scrollable-tags-list"
                      disabled={!isEditingPrimaryLanguage || !form.getFieldValue(['data', 'filterByTags'])}
                      tokenSeparators={[',']}
                      placeholder={t('Add tags for the article')}
                      options={
                        form.getFieldValue(['data', 'excludedTags'])?.length > 0 ? form.getFieldValue(['data', 'excludedTags'])?.map((tag) => {
                          return {
                            value: tag,
                            label: tag
                          };
                        }) : []
                      }
                      maxTagPlaceholder={(omittedValues) => (
                        <Tooltip
                          placement="bottomLeft"
                          title={
                            <div
                              className="overflow-y-auto overflow-x-hidden scrollbar-custom"
                              style={{ maxHeight: 220, maxWidth: 220 }}
                            >
                              {
                                omittedValues.map((value, index) => (
                                  <Typography.Paragraph ellipsis key={index} className="fs-base text-muted mb-0">
                                    {value.label}

                                    <Button
                                      size="small"
                                      type="link"
                                      className="mb-1"
                                      icon={<CloseOutlined className="text-secondary mr-0 fs-sm" />}
                                    />
                                  </Typography.Paragraph>
                                ))
                              }
                            </div>
                          }
                        >
                          +{omittedValues.length}
                        </Tooltip>
                      )}
                    />
                  </Form.Item>
                </Card>
              </Col>
            </Row>

            <Form.Item
              name={['data', 'query']}
              label={
                <Typography.Text>
                  {t('Query')}
                  <Tooltip
                    className="ml-1"
                    placement="top"
                    title={t('The semantic search query to be performed over the connected data sources.')}
                  >
                    <QuestionCircleOutlined className="fs-sm position-absolute" />
                  </Tooltip>
                </Typography.Text>
              }
              rules={[{
                required: true,
                message: `${t('This is a required field')}.`
              }]}
            >
              <RichTextEditor
                toolbarActions={[RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_VARIABLE]}
                placeholder={t('Enter value')}
                className="ql-sm ql-border-md ql-border-focus ql-font-normal w-100"
                hasFloatingToolbar={true}
                handleAsPlainText={true}
                shouldAutoFocus={false}
                disabled={!isEditingPrimaryLanguage}
              />
            </Form.Item>

            <Form.Item
              name={['data', 'variableName']}
              label={
                <Typography.Text>
                  {t('Variable name')}
                  <Tooltip
                    className="ml-1"
                    placement="top"
                    title={t('It is used for storing the answer to this search. Set a variable name to use the answer in messages templates, or in control flow nodes.')}
                  >
                    <QuestionCircleOutlined className="fs-sm position-absolute" />
                  </Tooltip>
                </Typography.Text>
              }
              rules={[{
                pattern: /^[a-zA-Z0-9_$\\.]*$/,
                message: t('Variable names can only use letters, numbers, underscores, dollar signs, and dots.')
              }]}
            >
              <Input
                placeholder={t('Enter the variable name')}
                size="large"
                maxLength={30}
                disabled={!isEditingPrimaryLanguage}
              />
            </Form.Item>
          </Fragment>
        ) : (
          <Empty
            description={
              <Typography.Text type="secondary">
                {t('No plugin enabled. Please')}
                <Button
                  type="link"
                  className="px-1"
                  onClick={() => {
                    navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId: tenantId })}?view=plugins&scope=LLM`);
                  }}
                >
                  {t('visit the plugins page')}
                </Button>
                {t('to enable a new one.')}
              </Typography.Text>}
          />
        )
      }
    </Fragment>
  );
};

SemanticSearchNodeForm.propTypes = {
  form: PropTypes.object.isRequired,
  selectedElement: PropTypes.object.isRequired
};

export default SemanticSearchNodeForm;
